import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  DataService as CoreDataService,
  ObjectKey,
  PersistenceService,
  PersistMode,
  PersistObjectModel,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
  ReadListDataSourceModel,
  TargetObjectData,
} from "@barthauer/abc/core/data";
import { Address } from "@barthauer/custom/arena";
import { Project } from "@barthauer/custom/xmt-models";
import { BehaviorSubject, Observable, ReplaySubject, Subject } from "rxjs";
import { ObjectKeys } from "../../services/data.service";

export enum ObjectTypeNames {
  Company = 111000100,
}
export enum WebStorageType {
  SessionStorage = "sessionStorage",
  LocalStorage = "localStorage",
}
export interface IProjectDependency {
  name: string;
  value?: string;
  storageType: WebStorageType;
}

@Injectable({
  providedIn: "root",
})
export class CompaniesService {
  private readonly objectSpaceName = "ABC.Applications.KAOS.Data";
  private gridDataSubject = new Subject<any>();
  private companyNameSubject = new Subject();
  private filterCompanyDataSubject = new Subject();
  private selectedCompanyDataSubject = new ReplaySubject<any>(1);
  private selectedProjectDataSubject = new Subject<Project>();
  public updateGridData$ = this.gridDataSubject.asObservable();
  public companyName$ = this.companyNameSubject.asObservable();
  public filterCompanyData$ = this.filterCompanyDataSubject.asObservable();
  public selectedCompanyData$ = this.selectedCompanyDataSubject.asObservable();

  constructor(
    private router: Router,
    private coreDataService: CoreDataService,
    private persistenceService: PersistenceService,
  ) {}

  public projectColumns = [
    this.coreDataService.createQueryColumn(
      "Id",
      "Id",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "Designation",
      "Label",
      QueryColumnSortOrder.None,
    ),

    // Parent
    this.coreDataService.createQueryColumn(
      "!.name",
      "ParentAddress",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "ParentId",
      "ParentId",
      QueryColumnSortOrder.None,
    ),

    this.coreDataService.createQueryColumn(
      "SysDateInsert",
      "Date",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "Description",
      "Description",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "StartDate",
      "StartDate",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "EndDate",
      "EndDate",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "State",
      "State",
      QueryColumnSortOrder.None,
    ),

    this.coreDataService.createQueryColumn(
      "Count(subProjects)",
      "subProjectsCount",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "Count(subProjects.Measures)",
      "measuresCount",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "Count(subProjects.Measures.MeasureTasks)",
      "tasksCount",
      QueryColumnSortOrder.None,
    ),

    this.coreDataService.createQueryColumn(
      "SysDateUpdate",
      "SysDateUpdate",
      QueryColumnSortOrder.Descending,
    ),
    this.coreDataService.createQueryColumn(
      "FormatTime(SysTimeUpdate, 'hh:mm:ss')",
      "SysTimeUpdate",
      QueryColumnSortOrder.Descending,
    ),
  ];
  public projectPersonColumns = [
    this.coreDataService.createQueryColumn(
      "Project.Designation",
      "Label",
      QueryColumnSortOrder.None,
    ),

    // Parent
    this.coreDataService.createQueryColumn(
      "!.!.name",
      "ParentAddress",
      QueryColumnSortOrder.None,
    ),

    this.coreDataService.createQueryColumn(
      "ParentId",
      "Id",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "Project.ParentId",
      "AddressId",
      QueryColumnSortOrder.None,
    ),

    this.coreDataService.createQueryColumn(
      "SysDateInsert",
      "Date",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "Project.Subject",
      "Subject",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "Project.StartDate",
      "StartDate",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "Project.EndDate",
      "EndDate",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "Project.State",
      "State",
      QueryColumnSortOrder.None,
    ),

    this.coreDataService.createQueryColumn(
      "Count(Project.subProjects)",
      "subProjectsCount",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "Count(Project.subProjects.Measures)",
      "measuresCount",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "Count(Project.subProjects.Measures.MeasureTasks)",
      "tasksCount",
      QueryColumnSortOrder.None,
    ),

    this.coreDataService.createQueryColumn(
      "SysDateUpdate",
      "SysDateUpdate",
      QueryColumnSortOrder.Descending,
    ),
    this.coreDataService.createQueryColumn(
      "FormatTime(SysTimeUpdate, 'hh:mm:ss')",
      "SysTimeUpdate",
      QueryColumnSortOrder.Descending,
    ),
  ];

  public loadListDataSource(
    objectType: string,
    columnName: string,
  ): Observable<any> {
    const readListDataSourceQuery = new ReadListDataSourceModel();
    const objectKey = new ObjectKey();
    objectKey.ObjectType = objectType;
    readListDataSourceQuery.ObjectKey = objectKey;
    readListDataSourceQuery.OutputMode = 0;
    readListDataSourceQuery.ColumnName = columnName;
    return this.coreDataService.executeReadListDataSource(
      readListDataSourceQuery,
    );
  }

  public readObjects<T>(
    objectType: string,
    columns: QueryColumn[],
    OPath?: string,
  ): Observable<any> {
    const readObjectQuery: QueryObjectsModel = new QueryObjectsModel();
    const mainQuery: Query = new Query();
    mainQuery.ObjectType = objectType;
    mainQuery.Columns = columns;
    mainQuery.OPath = OPath;
    if (OPath) {
      mainQuery.OPath = OPath;
    }
    readObjectQuery.ObjectQueries = [mainQuery];
    return this.coreDataService.executeReadObjectsQuery(readObjectQuery);
  }

  public deleteObject(
    objectKey: ObjectKeys,
    id: string | number,
  ): Observable<any> {
    const persistQuery: PersistObjectModel = new PersistObjectModel();
    persistQuery.ObjectSpaceName = this.objectSpaceName;
    const targetObject: TargetObjectData = new TargetObjectData();
    targetObject.Mode = PersistMode.Delete;
    targetObject.ObjectKey = new ObjectKey();
    targetObject.ObjectKey.Id = id.toString();
    targetObject.ObjectKey.ObjectType = objectKey;
    persistQuery.Object = targetObject;
    return this.persistenceService.executePersistObjectQuery(persistQuery);
  }

  public updateGridData(data: any) {
    this.gridDataSubject.next(data);
  }

  public prepareTitleCompany(companyName: string): void {
    this.companyNameSubject.next(companyName);
  }

  public filterCompanyData(data: Address[] | number) {
    this.filterCompanyDataSubject.next(data);
  }

  public selectedCompanyData(data: boolean) {
    this.selectedCompanyDataSubject.next(data);
  }

  public selectedProjectData(data: Project) {
    this.selectedProjectDataSubject.next(data);
  }

  public goToCompanyProjects(
    configDep: IProjectDependency[][],
    url: string,
  ): void {
    configDep[1].forEach((dep) => {
      window[dep.storageType].removeItem(dep.name);
    });
    window[configDep[0][0].storageType].setItem(
      configDep[0][0].name,
      configDep[0][0].value,
    );
    window[configDep[0][1].storageType].setItem(
      configDep[0][1].name,
      configDep[0][1].value,
    );
    this.prepareTitleCompany(configDep[0][1].value);
    this.router.navigateByUrl(url);
  }
}
